/**
 * Typing lock — keep the mic from hearing the keyboard.
 *
 * In voice mode the mic is live, so the SOUND of typing gets captured,
 * transcribed and auto-sent (the AI then answers keyboard clatter). Voice and
 * typing are separate input channels now (voice sends its own transcript; the
 * composer holds only the typed draft), so the lock's only job is acoustic: mic
 * off while keys are actively flying, back on shortly after they stop. A typed
 * draft may sit in the composer WHILE the user speaks — the mic resuming can't
 * hurt it, so release is idle-based, not tied to an empty box.
 *
 * Same shape as coach-playback / manual-replay: `useVoiceInput.start()` gates on
 * `isUserTyping()`, and subscriptions stop the mic when it flips on / resume it
 * when it flips off.
 */
type Listener = (typing: boolean) => void;

const listeners = new Set<Listener>();
let typing = false;
let idle: ReturnType<typeof setTimeout> | null = null;

/** Quiet period after the last keystroke before the mic may resume. */
const IDLE_MS = 1200;

function set(next: boolean): void {
  if (typing === next) return;
  typing = next;
  listeners.forEach((l) => l(next));
}

export function isUserTyping(): boolean {
  return typing;
}

export function subscribeTyping(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** A real keystroke landed — hold the mic off, and (re)arm the idle release. */
export function noteTyping(): void {
  set(true);
  if (idle) clearTimeout(idle);
  idle = setTimeout(() => {
    idle = null;
    set(false);
  }, IDLE_MS);
}

/** Message sent / composer cleared — release the mic immediately. */
export function endTyping(): void {
  if (idle) {
    clearTimeout(idle);
    idle = null;
  }
  set(false);
}
